"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { fillRandomFloats } from "@llmgateway/shared/random";
interface FlickeringGridProps extends React.HTMLAttributes<HTMLDivElement> {
  squareSize?: number;
  gridGap?: number;
  flickerChance?: number;
  color?: string;
  width?: number;
  height?: number;
  className?: string;
  maxOpacity?: number;
}
export const FlickeringGrid: React.FC<FlickeringGridProps> = t0 => {
  const $ = _c(38);
  let className;
  let height;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let width;
  if ($[0] !== t0) {
    ({
      squareSize: t1,
      gridGap: t2,
      flickerChance: t3,
      color: t4,
      width,
      height,
      className,
      maxOpacity: t5,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = height;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = width;
  } else {
    className = $[1];
    height = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    width = $[9];
  }
  const squareSize = t1 === undefined ? 4 : t1;
  const gridGap = t2 === undefined ? 6 : t2;
  const flickerChance = t3 === undefined ? 0.3 : t3;
  const color = t4 === undefined ? "rgb(0, 0, 0)" : t4;
  const maxOpacity = t5 === undefined ? 0.3 : t5;
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const isInViewRef = useRef(false);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      width: 0,
      height: 0
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const [canvasSize, setCanvasSize] = useState(t6);
  const toRGBA = _temp;
  const memoizedColor = toRGBA(color);
  let t7;
  if ($[11] !== gridGap || $[12] !== maxOpacity || $[13] !== squareSize) {
    t7 = (canvas_0, width_0, height_0) => {
      const dpr = window.devicePixelRatio || 1;
      canvas_0.width = width_0 * dpr;
      canvas_0.height = height_0 * dpr;
      canvas_0.style.width = `${width_0}px`;
      canvas_0.style.height = `${height_0}px`;
      const cols = Math.ceil(width_0 / (squareSize + gridGap));
      const rows = Math.ceil(height_0 / (squareSize + gridGap));
      const squares = new Float32Array(cols * rows);
      fillRandomFloats(squares);
      for (let i = 0; i < squares.length; i++) {
        squares[i] = squares[i] * maxOpacity;
      }
      const noise = new Float32Array(squares.length * 2);
      return {
        cols,
        rows,
        squares,
        noise,
        dpr
      };
    };
    $[11] = gridGap;
    $[12] = maxOpacity;
    $[13] = squareSize;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const setupCanvas = t7;
  let t8;
  if ($[15] !== flickerChance || $[16] !== maxOpacity) {
    t8 = (squares_0, noise_0, deltaTime) => {
      fillRandomFloats(noise_0);
      const flickerThreshold = flickerChance * deltaTime;
      for (let i_0 = 0; i_0 < squares_0.length; i_0++) {
        if (noise_0[i_0] < flickerThreshold) {
          squares_0[i_0] = noise_0[squares_0.length + i_0] * maxOpacity;
        }
      }
    };
    $[15] = flickerChance;
    $[16] = maxOpacity;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const updateSquares = t8;
  let t9;
  if ($[18] !== gridGap || $[19] !== memoizedColor || $[20] !== squareSize) {
    t9 = (ctx_0, width_1, height_1, cols_0, rows_0, squares_1, dpr_0) => {
      ctx_0.clearRect(0, 0, width_1, height_1);
      ctx_0.fillStyle = `${memoizedColor}1)`;
      for (let i_1 = 0; i_1 < cols_0; i_1++) {
        const squareIndex = i_1 * rows_0;
        for (let j = 0; j < rows_0; j++) {
          ctx_0.globalAlpha = squares_1[squareIndex + j];
          ctx_0.fillRect(i_1 * (squareSize + gridGap) * dpr_0, j * (squareSize + gridGap) * dpr_0, squareSize * dpr_0, squareSize * dpr_0);
        }
      }
      ctx_0.globalAlpha = 1;
    };
    $[18] = gridGap;
    $[19] = memoizedColor;
    $[20] = squareSize;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const drawGrid = t9;
  let t10;
  let t11;
  if ($[22] !== drawGrid || $[23] !== height || $[24] !== setupCanvas || $[25] !== updateSquares || $[26] !== width) {
    t10 = () => {
      const canvas_1 = canvasRef.current;
      const container = containerRef.current;
      const ctx_1 = canvas_1?.getContext("2d") ?? null;
      let animationFrameId = null;
      let resizeObserver = null;
      let intersectionObserver = null;
      let gridParams = null;
      if (canvas_1 && container && ctx_1) {
        const updateCanvasSize = () => {
          const newWidth = width || container.clientWidth;
          const newHeight = height || container.clientHeight;
          setCanvasSize({
            width: newWidth,
            height: newHeight
          });
          gridParams = setupCanvas(canvas_1, newWidth, newHeight);
        };
        updateCanvasSize();
        let lastTime = 0;
        const animate = time => {
          if (!isInViewRef.current || !gridParams) {
            return;
          }
          const deltaTime_0 = (time - lastTime) / 1000;
          lastTime = time;
          updateSquares(gridParams.squares, gridParams.noise, deltaTime_0);
          drawGrid(ctx_1, canvas_1.width, canvas_1.height, gridParams.cols, gridParams.rows, gridParams.squares, gridParams.dpr);
          animationFrameId = requestAnimationFrame(animate);
        };
        resizeObserver = new ResizeObserver(() => {
          updateCanvasSize();
        });
        resizeObserver.observe(container);
        intersectionObserver = new IntersectionObserver(t12 => {
          const [entry] = t12;
          const wasInView = isInViewRef.current;
          isInViewRef.current = entry.isIntersecting;
          if (entry.isIntersecting && !wasInView) {
            lastTime = performance.now();
            animationFrameId = requestAnimationFrame(animate);
          }
        }, {
          threshold: 0
        });
        intersectionObserver.observe(canvas_1);
      }
      return () => {
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }
      };
    };
    t11 = [setupCanvas, updateSquares, drawGrid, width, height];
    $[22] = drawGrid;
    $[23] = height;
    $[24] = setupCanvas;
    $[25] = updateSquares;
    $[26] = width;
    $[27] = t10;
    $[28] = t11;
  } else {
    t10 = $[27];
    t11 = $[28];
  }
  useEffect(t10, t11);
  let t12;
  if ($[29] !== className) {
    t12 = cn("h-full w-full", className);
    $[29] = className;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== canvasSize.height || $[32] !== canvasSize.width) {
    t13 = <canvas ref={canvasRef} className="pointer-events-none" style={{
      width: canvasSize.width,
      height: canvasSize.height
    }} />;
    $[31] = canvasSize.height;
    $[32] = canvasSize.width;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== props || $[35] !== t12 || $[36] !== t13) {
    t14 = <div ref={containerRef} className={t12} {...props}>{t13}</div>;
    $[34] = props;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
function _temp(color_0) {
  if (typeof window === "undefined") {
    return "rgba(0, 0, 0,";
  }
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return "rgba(255, 0, 0,";
  }
  ctx.fillStyle = color_0;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b] = Array.from(ctx.getImageData(0, 0, 1, 1).data);
  return `rgba(${r}, ${g}, ${b},`;
}